import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import { Key, AlertTriangle, Mail, ArrowLeft, Check } from "lucide-react";
import {
  validateEmail,
  validatePassword,
  isAccountLocked,
  recordFailedLogin,
  resetLoginAttempts,
  getRemainingAttempts,
  getLoginDelay,
} from "@/lib/security";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

type AuthStep = "form" | "verify-email";

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState<AuthStep>("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [cdkCode, setCdkCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockStatus, setLockStatus] = useState<{ locked: boolean; remainingTime?: number }>({ locked: false });
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading: isVerifying, countdown, requestVerification, verifyCode } = useEmailVerification();

  // 检查账户锁定状态
  useEffect(() => {
    const checkLock = () => {
      const status = isAccountLocked();
      setLockStatus(status);
      setRemainingAttempts(getRemainingAttempts());
    };
    
    checkLock();
    const interval = setInterval(checkLock, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleSendVerification = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!validateEmail(normalizedEmail)) {
      toast({
        title: "邮箱格式错误",
        description: "请输入有效的邮箱地址",
        variant: "destructive",
      });
      return;
    }

    const pwdValidation = validatePassword(password);
    if (!pwdValidation.valid) {
      toast({
        title: "密码不符合要求",
        description: pwdValidation.message,
        variant: "destructive",
      });
      return;
    }

    const trimmedCdk = cdkCode.trim().toUpperCase();
    const result = await requestVerification({
      email: normalizedEmail,
      type: "registration",
      cdkCode: trimmedCdk ? trimmedCdk : undefined,
    });
    if (result.success) {
      setStep("verify-email");
      toast({
        title: "验证码已发送",
        description: "请查收邮件并输入验证码",
      });
    } else {
      toast({
        title: "发送失败",
        description: result.error || "请稍后再试",
        variant: "destructive",
      });
    }
  };

  const handleVerifyAndRegister = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: "请输入完整验证码",
        description: "验证码为6位数字",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // 先验证验证码
      const normalizedEmail = email.trim().toLowerCase();
      const verifyResult = await verifyCode(normalizedEmail, verificationCode, "registration");
      if (!verifyResult.success || !verifyResult.verified) {
        toast({
          title: "验证失败",
          description: verifyResult.error || "验证码无效或已过期",
          variant: "destructive",
        });
        return;
      }

      // 验证通过，执行注册
      const trimmedCdk = cdkCode.trim().toUpperCase();
      const { error } = await signUp(normalizedEmail, password, nickname, trimmedCdk ? trimmedCdk : undefined);
      if (error) {
        toast({
          title: "注册失败",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "注册成功",
          description: "欢迎加入 dango！",
        });
        navigate("/");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (lockStatus.locked) {
      toast({
        title: "账户已锁定",
        description: `请在 ${lockStatus.remainingTime} 分钟后重试`,
        variant: "destructive",
      });
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!validateEmail(normalizedEmail)) {
      toast({
        title: "邮箱格式错误",
        description: "请输入有效的邮箱地址",
        variant: "destructive",
      });
      return;
    }

    const delay = getLoginDelay();
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    setIsSubmitting(true);
    try {
      // 随机触发验证码 (1/3 概率) 或 之前失败过多 (前端简单模拟，更安全的是后端返回需要验证码)
      // 这里为了防止恶意刷，我们强制 33% 概率触发，或者后端返回 CAPTCHA_REQUIRED 错误时触发
      // 但由于 signIn 是直接调用的 supabase.auth，我们无法拦截它的内部逻辑
      // 所以我们在前端做一层随机拦截
      
      const shouldVerify = Math.random() < 0.33; 
      
      if (shouldVerify) {
        try {
           // 这里需要引入 runAlicomCaptcha，由于文件头部没引入，我需要等会添加 import
           // 为了方便，我假设已经在组件外部或头部引入了
           // 实际代码中需要补充 import { runAlicomCaptcha } from "@/lib/aliyun-captcha";
           const { runAlicomCaptcha } = await import("@/lib/aliyun-captcha");
           await runAlicomCaptcha();
        } catch (e) {
          setIsSubmitting(false);
          return; // 用户取消验证或验证失败
        }
      }

      const { error } = await signIn(normalizedEmail, password);
      if (error) {
        const lockResult = recordFailedLogin();
        setRemainingAttempts(getRemainingAttempts());
        
        if (lockResult.locked) {
          setLockStatus(lockResult);
          toast({
            title: "登录失败次数过多",
            description: `账户已锁定 ${lockResult.remainingTime} 分钟`,
            variant: "destructive",
          });
        } else {
          const rawCode = (error as any)?.code;
          const message =
            rawCode === "invalid_credentials" || /invalid login credentials/i.test(error.message)
              ? "邮箱或密码错误（请使用注册邮箱登录，不是昵称）"
              : error.message;
          toast({
            title: "登录失败",
            description: `${message}（剩余尝试次数：${getRemainingAttempts()}）`,
            variant: "destructive",
          });
        }
      } else {
        resetLoginAttempts();
        toast({
          title: "登录成功",
          description: "欢迎回来！",
        });
        navigate("/");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLogin) {
      await handleLogin(e);
    } else {
      await handleSendVerification();
    }
  };

  const resetToForm = () => {
    setStep("form");
    setVerificationCode("");
  };

  // 验证码输入页面
  if (step === "verify-email") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent/30 px-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-3xl shadow-card border border-border/50 p-8">
            {/* 返回按钮 */}
            <button
              onClick={resetToForm}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              返回
            </button>

            {/* Logo */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-hero flex items-center justify-center shadow-lg">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-xl font-bold text-foreground mb-1">验证邮箱</h1>
              <p className="text-sm text-muted-foreground">
                验证码已发送至 <span className="text-foreground font-medium">{email}</span>
              </p>
            </div>

            {/* 验证码输入 */}
            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={verificationCode}
                  onChange={setVerificationCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={handleVerifyAndRegister}
                className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base rounded-xl shadow-md"
                disabled={isSubmitting || isVerifying || verificationCode.length !== 6}
              >
                {isSubmitting ? (
                  "注册中..."
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    验证并注册
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => requestVerification({ 
                    email: email.trim().toLowerCase(), 
                    type: "registration", 
                    cdkCode: cdkCode.trim().toUpperCase() 
                  })}
                  disabled={countdown > 0 || isVerifying}
                  className="text-sm text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                >
                  {countdown > 0 ? `重新发送 (${countdown}s)` : "重新发送验证码"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent/30 px-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-3xl shadow-card border border-border/50 p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-hero flex items-center justify-center shadow-lg">
              <span className="text-3xl">🍡</span>
            </div>
            <h1 className="text-xl font-bold text-foreground mb-1">dango</h1>
            <p className="text-sm text-muted-foreground">
              用耳朵感受日语之美
            </p>
          </div>

          {/* 锁定警告 */}
          {lockStatus.locked && (
            <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/20 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">账户已锁定</p>
                <p className="text-xs text-destructive/80 mt-1">
                  登录失败次数过多，请在 {lockStatus.remainingTime} 分钟后重试
                </p>
              </div>
            </div>
          )}

          {/* 剩余尝试次数警告 */}
          {!lockStatus.locked && isLogin && remainingAttempts < 5 && remainingAttempts > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                剩余尝试次数：{remainingAttempts}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cdkCode" className="flex items-center gap-2 text-foreground/80">
                    <Key className="h-4 w-4 text-primary" />
                    激活码（选填）
                  </Label>
                  <Input
                    id="cdkCode"
                    type="text"
                    value={cdkCode}
                    onChange={(e) => setCdkCode(e.target.value.toUpperCase())}
                    placeholder="请输入激活码"
                    className="font-mono tracking-wider text-center text-lg h-12 bg-muted/50"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    有卡密可直接升级，没有也可免费注册
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nickname" className="text-foreground/80">昵称</Label>
                  <Input
                    id="nickname"
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="请输入您的昵称"
                    className="bg-muted/50"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground/80">邮箱</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                required
                className="bg-muted/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground/80">密码</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-muted/50"
              />
              {!isLogin && (
                <p className="text-xs text-muted-foreground">
                  至少8位，包含字母和数字
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base rounded-xl shadow-md"
              disabled={isSubmitting || isVerifying || lockStatus.locked}
            >
              {isSubmitting || isVerifying 
                ? "处理中..." 
                : isLogin 
                  ? "登录" 
                  : "发送验证码"}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-3 text-muted-foreground">或者</span>
            </div>
          </div>

          {/* Toggle */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">
              {isLogin ? "还没有账号？" : "已有账号？"}
            </span>
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setStep("form");
                setVerificationCode("");
              }}
              className="ml-1 text-primary font-medium hover:underline"
            >
              {isLogin ? "立即注册" : "去登录"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          继续即表示您同意我们的服务条款和隐私政策
        </p>
        <p className="text-center text-xs text-muted-foreground mt-3">
          客服联系方式VX：shunlixiezuo168
        </p>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors mt-2">
          桂ICP备2026001540号-2
        </a>
      </div>
    </div>
  );
}
